import React, { Dispatch, SetStateAction, useState } from "react";
import TodayIcon from "@material-ui/icons/Today";
import {
  Button,
  Card,
  CardActions,
  CardHeader,
  Typography,
} from "@material-ui/core";
import { useSubscription } from "react-stomp-hooks";
import { useIntl } from "react-intl";
import { useStyles } from "./JoinMeetingPopUp.style";
import { SocketDestination } from "../../../WebSocket/WebSocketSession";
import notificationSound from "../../../Assets/Audios/notification.mp3";
import {
  parseSignalMessage,
  SignalAction,
  SignalType,
} from "../../../WebSocket/model/SignalMessage";
import { useCustomerId } from "../../../Providers/CustomerProvider/CustomerProvider";
import ConnectToMeetingButton from "../../Buttons/ConnectToMeetingButton/ConnectToMeetingButton";
import { Variant } from "../../BookingsList/Components/Variant/Variant";
import { Meeting } from "../../../Models/Meeting";
import { meetingService } from "../../../Providers/ServiceProvider/ServiceProvider";
import { getFormattedDate, getFormattedTimespan } from "../../../Utils/Meeting";
import { useAuthenticatedUser } from "../../../Providers/AuthenticatedUserProvider/AuthenticatedUserProvider";
import PermissionController from "../../PermissionController/PermissionController";
import { UserRoleType } from "../../../generated/user-api";
import { messages } from "./JoinMeetingPopUp.messages";

interface Props {
  children: React.ReactNode;
}

interface ContextType {
  showPopUp: boolean;
}

const initialState: ContextType = {
  showPopUp: false,
};

const Context = React.createContext<
  [ContextType, Dispatch<SetStateAction<ContextType>>]
>([initialState, () => {}]);

const JoinMeetingPopUp = (props: Props) => {
  const intl = useIntl();
  const { children } = props;
  const customerId = useCustomerId();
  const [authenticatedUser] = useAuthenticatedUser();
  const [state, setState] = useState(initialState);
  const [meeting, setMeeting] = useState<Meeting>();
  const classes = useStyles();
  const audio = new Audio(notificationSound);

  const handleClose = () => {
    setState({ showPopUp: false });
  };

  const loadMeeting = (meetingId: string) => {
    if (customerId) {
      meetingService()
        .getMeetingById({ customerId, meetingId })
        .then((res) => setMeeting(res));
    }
  };

  useSubscription(SocketDestination.SIGNAL_GLOBAL + customerId, (message) => {
    const signalMessage = parseSignalMessage(message.body);
    if (
      signalMessage.type === SignalType.MEETING_SESSION &&
      signalMessage.action === SignalAction.WAITING
    ) {
      if (signalMessage.triggeredBy !== authenticatedUser.user?.id) {
        if (!state.showPopUp) {
          loadMeeting(signalMessage.affectedIds[0]);
          setState({ showPopUp: true });
          audio.play();
        }
      }
    }
  });

  return (
    <Context.Provider value={[state, setState]}>
      <PermissionController allowedRoles={[UserRoleType.Staff]}>
        {meeting && (
          <Card
            className={classes.container}
            elevation={10}
            hidden={!state.showPopUp}
          >
            <Typography variant="h6" className={classes.title}>
              Deltagare väntar på dig
            </Typography>
            <CardHeader
              avatar={<TodayIcon />}
              title={meeting?.title}
              subheader={`${getFormattedDate(
                intl,
                meeting.startDateTime
              )} ${getFormattedTimespan(
                meeting.startDateTime,
                meeting.duration
              )}`}
              titleTypographyProps={{
                style: { fontSize: 16, fontWeight: 600 },
              }}
              subheaderTypographyProps={{ className: classes.subheader }}
            />
            <CardActions className={classes.cardActions}>
              <Button
                variant="text"
                size="small"
                className={classes.button}
                onClick={handleClose}
              >
                {intl.formatMessage(messages.closeButtonLabel)}
              </Button>
              {meeting && (
                <ConnectToMeetingButton
                  contextVariant={Variant.DASHBOARD_UPCOMING_BOOKINGS}
                  meeting={meeting}
                />
              )}
            </CardActions>
          </Card>
        )}
      </PermissionController>
      {children}
    </Context.Provider>
  );
};

export default JoinMeetingPopUp;
